import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient, { Evidence } from '../api/client';
import { ArrowLeft, FileBox, Video, Image, FileText, Music, Clock, Shield } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { useState, useEffect } from 'react';

function evidenceIcon(type: string) {
  const t = type.toLowerCase();
  if (t.includes('video') || t.includes('mp4')) return <Video className="h-4 w-4" />;
  if (t.includes('image') || t.includes('jpg') || t.includes('png')) return <Image className="h-4 w-4" />;
  if (t.includes('audio') || t.includes('mp3') || t.includes('wav')) return <Music className="h-4 w-4" />;
  if (t.includes('pdf') || t.includes('doc')) return <FileText className="h-4 w-4" />;
  return <FileBox className="h-4 w-4" />;
}

function EvidenceThumbnail({ evidence }: { evidence: Evidence }) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(evidence.thumbnailUrl || null);
  const isImage = evidence.fileType?.startsWith('image/');

  useEffect(() => {
    if (!thumbUrl && isImage && evidence.id) {
      apiClient.getPreviewBlobUrl(evidence.id).then(setThumbUrl).catch(() => {});
    }
  }, [thumbUrl, isImage, evidence.id]);

  if (thumbUrl) {
    return <img src={thumbUrl} alt="" className="h-12 w-16 object-cover rounded" />;
  }
  return (
    <div className="h-12 w-16 bg-slate-700 rounded flex items-center justify-center text-slate-400">
      {evidenceIcon(evidence.fileType || evidence.evidenceType)}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CaseDetail() {
  const { caseNumber } = useParams<{ caseNumber: string }>();

  const { data: caseData, isLoading: caseLoading } = useQuery({
    queryKey: ['case', caseNumber],
    queryFn: () => apiClient.getCase(caseNumber!),
    enabled: !!caseNumber,
  });

  const { data: evidence, isLoading: evidenceLoading } = useQuery({
    queryKey: ['evidence', caseNumber],
    queryFn: () => apiClient.getCaseEvidence(caseNumber!),
    enabled: !!caseNumber,
  });

  if (caseLoading) {
    return <div className="text-slate-400">Loading case...</div>;
  }

  if (!caseData) {
    return <div className="text-slate-400">Case not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/cases" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to cases
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="text-xs font-mono text-rtcc-accent">{caseData.caseNumber}</span>
            <h1 className="text-2xl font-bold text-white mt-1">{caseData.title}</h1>
            {caseData.description && (
              <p className="text-slate-400 text-sm mt-2">{caseData.description}</p>
            )}
          </div>
          <span className={`text-xs px-3 py-1 rounded-full font-medium ${
            caseData.status === 'open' || caseData.status === 'Active'
              ? 'bg-rtcc-accent/20 text-rtcc-accent'
              : 'bg-slate-700 text-slate-400'
          }`}>
            {caseData.status}
          </span>
        </div>
      </div>

      {/* Case Info */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {caseData.type && <InfoCard label="Type" value={caseData.type} />}
        {caseData.court && <InfoCard label="Jurisdiction" value={caseData.court} />}
        {caseData.leadInvestigator && <InfoCard label="Lead Investigator" value={caseData.leadInvestigator} />}
        <InfoCard label="Filed" value={format(new Date(caseData.createdAt), 'MMM d, yyyy')} />
      </div>

      {/* Evidence List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Evidence ({evidence?.length || 0})</h2>
          <Link
            to={`/upload?case=${caseNumber}`}
            className="text-sm text-rtcc-accent hover:text-rtcc-accent/80 transition-colors"
          >
            + Upload
          </Link>
        </div>

        {evidenceLoading ? (
          <div className="text-slate-400 text-sm">Loading evidence...</div>
        ) : !evidence?.length ? (
          <div className="text-center py-12 bg-rtcc-panel rounded-lg border border-slate-700">
            <FileBox className="h-8 w-8 mx-auto mb-2 text-slate-600" />
            <p className="text-slate-500 text-sm">No evidence files yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {evidence.map((e) => (
              <Link
                key={e.id}
                to={`/media/${e.id}`}
                className="flex items-center gap-4 p-4 bg-rtcc-panel rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
              >
                {/* Thumbnail or icon */}
                <EvidenceThumbnail evidence={e} />

                <div className="flex-1 min-w-0">
                  <h3 className="text-white text-sm font-medium truncate">{e.title || e.fileName}</h3>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                    <span>{e.evidenceType}</span>
                    <span>{formatFileSize(e.fileSize)}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNow(new Date(e.uploadedAt), { addSuffix: true })}
                    </span>
                  </div>
                </div>

                {/* Integrity badge */}
                <div className={`flex items-center gap-1 text-xs ${
                  e.integrityStatus === 'verified' ? 'text-rtcc-accent' : 'text-rtcc-warning'
                }`}>
                  <Shield className="h-3 w-3" />
                  {e.integrityStatus}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 bg-rtcc-panel rounded-lg border border-slate-700">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="text-sm text-white font-medium truncate">{value}</p>
    </div>
  );
}
